import React, { useMemo, useState } from 'react'
import { Card, Grid, LinearProgress, Typography } from '@material-ui/core'
import { SortByDateSelect } from '../../shared/sort-by-date-select'
import { AppPagination } from '../../components/app-pagination'
import { useTranslation } from 'react-i18next'
import { useOrdersListPageStyles } from './orders-list-page.styles'
import { useOrdersListPage } from './orders-list-page.hook'
import { OrderStatus } from '../order-page/order-header/order-header.constants'
import { OrderListItem } from '../../services/api/orders/models'
import {
  OrdersListItemCard,
  OrdersListItemCardPlaceholder,
  ORDER_LIST_PLACEHOLDER_COUNT,
} from './order-list-item'
import { PageWrapper } from '../../components/page-wrapper'
import { BreadcrumbItem } from '../../components/breadcrumbs'
import { LabeledSelect } from '../../shared/labeled-select'
import { useScreenSize } from '../../hooks/use-screen-size'
import { ScreenSize } from '../../settings/device'

export const OrdersListPage: React.FC = () => {
  const classes = useOrdersListPageStyles()
  const { t } = useTranslation()
  const screenSize = useScreenSize()
  const [activeTab, setActiveTab] = useState<0 | 1>(0)
  const {
    purchasedOrders,
    soldOrders,
    loading,
    loadingPurchased,
    loadingSold,
    error,
    purchasedQuery,
    soldQuery,
    purchasedTotalPages,
    soldTotalPages,
    onPurchasedPageChange,
    onSoldPageChange,
    onPurchasedSortChange,
    onSoldSortChange,
  } = useOrdersListPage(activeTab)

  const activeQuery = activeTab === 0 ? purchasedQuery : soldQuery
  const onSortChange = activeTab === 0 ? onPurchasedSortChange : onSoldSortChange

  const ordersBreadcrumbItems: BreadcrumbItem[] = useMemo(
    () => [
      { label: t('header.menu.search'), to: '/search' },
      { label: t('ordersListPage.title') },
    ],
    [t]
  )

  const orderTypeOptions = useMemo(
    () => [
      { value: 0, label: t('ordersListPage.tabPurchased') },
      { value: 1, label: t('ordersListPage.tabSold') },
    ],
    [t]
  )

  const handleOrderTypeChange = (newValue: number) => {
    setActiveTab(newValue as 0 | 1)
  }

  const statusLabel = (status: number) =>
    t(`orderPage.${OrderStatus.get(status) ?? 'Pending'}`)

  const controls = (
    <div className={classes.queryParamsHolder}>
      <LabeledSelect
        label={t('ordersListPage.orderType')}
        value={activeTab}
        options={orderTypeOptions}
        onChange={handleOrderTypeChange}
        idSuffix="orders-type"
        className={classes.mobileSelect}
        fluid
      />
      <SortByDateSelect
        value={activeQuery.sortByDate}
        onChange={onSortChange}
        disabled={loading}
        idSuffix={`orders-tab-${activeTab}`}
        className={classes.mobileSelect}
        fluid
      />
    </div>
  )

  return (
    <>
      <LinearProgress style={{ visibility: loading ? 'visible' : 'hidden' }} />
      <PageWrapper
        title={t('ordersListPage.title')}
        displayGoBackButton={false}
        breadcrumbItems={ordersBreadcrumbItems}
        titleEndContent={screenSize === ScreenSize.Desktop ? controls : undefined}
      >
        {screenSize !== ScreenSize.Desktop && (
          <div className={classes.mobileControls}>{controls}</div>
        )}

        {loading && (
          <span className={classes.visuallyHidden} aria-live="polite">
            {t('ordersListPage.loadingOrders')}
          </span>
        )}

        {error && (
          <Typography color="error" style={{ marginBottom: 16 }}>
            {error.getErrorMessage(t)}
          </Typography>
        )}

        {activeTab === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {loadingPurchased &&
                Array.from({ length: ORDER_LIST_PLACEHOLDER_COUNT }, (_, i) => (
                  <OrdersListItemCardPlaceholder key={`p-ph-${i}`} />
                ))}
              {!loadingPurchased && purchasedOrders.length === 0 && (
                <Card className={classes.emptyState} variant="outlined">
                  <Typography>{t('ordersListPage.emptyPurchased')}</Typography>
                </Card>
              )}
              {!loadingPurchased &&
                purchasedOrders.map((order: OrderListItem) => (
                  <OrdersListItemCard
                    key={order.orderId}
                    order={order}
                    statusLabel={statusLabel(order.status)}
                  />
                ))}
              {activeTab === 0 &&
                !loadingPurchased &&
                purchasedTotalPages > 1 && (
                  <AppPagination
                    spacing="toolbar"
                    count={purchasedTotalPages}
                    disabled={loading}
                    page={purchasedQuery.pageIndex + 1}
                    onChange={(_, page) => onPurchasedPageChange(page - 1)}
                  />
                )}
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {loadingSold &&
                Array.from({ length: ORDER_LIST_PLACEHOLDER_COUNT }, (_, i) => (
                  <OrdersListItemCardPlaceholder key={`s-ph-${i}`} />
                ))}
              {!loadingSold && soldOrders.length === 0 && (
                <Card className={classes.emptyState} variant="outlined">
                  <Typography>{t('ordersListPage.emptySold')}</Typography>
                </Card>
              )}
              {!loadingSold &&
                soldOrders.map((order: OrderListItem) => (
                  <OrdersListItemCard
                    key={order.orderId}
                    order={order}
                    statusLabel={statusLabel(order.status)}
                    showViewOrderLink
                  />
                ))}
              {!loadingSold && soldTotalPages > 1 && (
                <AppPagination
                  spacing="toolbar"
                  count={soldTotalPages}
                  disabled={loading}
                  page={soldQuery.pageIndex + 1}
                  onChange={(_, page) => onSoldPageChange(page - 1)}
                />
              )}
            </Grid>
          </Grid>
        )}
      </PageWrapper>
    </>
  )
}
